import React, { ChangeEvent, useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import {
  AuInputLabel,
  AuErrorText,
  AuHintText,
  AuFormItemWrapper
} from 'components/DTA';

import './AuDateInput.scss';

interface IAuDateInputProps {
  name: string;
  label?: string;
  hintText?: string | React.ReactNode;
  className?: string;
  dark?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  reset?: boolean;
  callback?(day: string, month: string, year: string): void;
}

const AuDateInput: React.FC<IAuDateInputProps> = ({ name, label, hintText, dark, disabled, reset, className = '', callback }) => {
  const { errors, getFieldMeta, setFieldValue } = useFormikContext();
  const fieldError = get(errors, name);
  const currentFieldValue = getFieldMeta<string>(name).value;

  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const numberRegexPattern = new RegExp('^-?\\d+$');
  const dayMax = 31;
  const monthMax = 12;
  const yearMax = 9999;

  useEffect(() => {
    if (currentFieldValue) {
      const initialValue = moment(currentFieldValue);
      const day = initialValue.date().toString();
      const month = (1 + initialValue.month()).toString();
      const year = initialValue.year().toString();

      if (numberRegexPattern.test(day)) {
        setDay(day);
      }
      if (numberRegexPattern.test(month)) {
        setMonth(month);
      }
      if (numberRegexPattern.test(year)) {
        setYear(year);
      }
    }
    else if (currentFieldValue === null || currentFieldValue === '') {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, []);

  useEffect(() => {
    setDateValue();
  }, [day, month, year]);

  useEffect(() => {
    if (reset === true && (currentFieldValue === null || currentFieldValue === '')) {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, [currentFieldValue]);

  const setDateValue = () => {
    const inputValue = year + '-' + month + '-' + day;
    if (!isEmpty(day) && !isEmpty(month) && !isEmpty(year)) {
      const dateValue = moment(inputValue, 'YYYY-M-D', true);
      if (dateValue.isValid()) {
        setFieldValue(name, dateValue.format('YYYY-MM-DD'));
        if (callback) {
          callback(day, month, year);
        }
      }
      else {
        setFieldValue(name, inputValue);
      }
    }
    else if (!isEmpty(day) || !isEmpty(month) || !isEmpty(year)) {
      setFieldValue(name, inputValue);
    }
    else {
      setFieldValue(name, '');
    }
  };

  const handleOnChangeDay = (event: ChangeEvent<HTMLInputElement>) => {
    if (isEmpty(event.target.value)
      || event.target.value === '0') {
      setDay(event.target.value);
    }
    else {
      const currentValue = !isEmpty(event.target.value) && numberRegexPattern.test(event.target.value) ? parseInt(event.target.value) : null;
      if (currentValue && currentValue >= 0 && currentValue <= dayMax) {
        setDay(event.target.value);
      }
    }
  };

  const handleOnChangeMonth = (event: ChangeEvent<HTMLInputElement>) => {
    if (isEmpty(event.target.value)
      || event.target.value === '0') {
      setMonth(event.target.value);
    }
    else {
      const currentValue = !isEmpty(event.target.value) && numberRegexPattern.test(event.target.value) ? parseInt(event.target.value) : null;
      if (currentValue && currentValue >= 0 && currentValue <= monthMax) {
        setMonth(event.target.value);
      }
    }
  };

  const handleOnChangeYear = (event: ChangeEvent<HTMLInputElement>) => {
    if (isEmpty(event.target.value)) {
      setYear(event.target.value);
    }
    else {
      const currentValue = !isEmpty(event.target.value) && numberRegexPattern.test(event.target.value) ? parseInt(event.target.value) : null;
      if (currentValue && currentValue >= 1 && currentValue <= yearMax) {
        setYear(event.target.value);
      }
    }
  };

  return (
    <AuFormItemWrapper>
      {
        label && <AuInputLabel text={label} htmlFor={name} />
      }
      {
        hintText && <AuHintText text={hintText} htmlFor={name} />
      }
      <Field
        name={name}
        onChange={setDateValue}>
        {() => (
          <>
            <div style={{ display: 'flex' }}>
              <div className='au-number-input-wrapper'>
                <AuInputLabel text='Date' htmlFor='Date' />
                <div>
                  <input
                    type='text'
                    placeholder='DD'
                    value={day}
                    onChange={handleOnChangeDay}
                    disabled={disabled}
                    className={
                      `au-text-input ${className} au-text-input--number au-text-input--width-md` +
                      `${dark ? ' au-text-input--dark' : ''}` +
                      `${getFieldMeta(name).error && !disabled ? ' au-text-input--invalid' : ''}`
                    } />
                </div>
              </div>
              <div className='au-number-input-wrapper'>
                <AuInputLabel text='Month' htmlFor='Month' />
                <div>
                  <input
                    type='text'
                    placeholder='MM'
                    value={month}
                    onChange={handleOnChangeMonth}
                    disabled={disabled}
                    className={
                      `au-text-input ${className} au-text-input--number au-text-input--width-md` +
                      `${dark ? ' au-text-input--dark' : ''}` +
                      `${getFieldMeta(name).error && !disabled ? ' au-text-input--invalid' : ''}`
                    } />
                </div>
              </div>
              <div className='au-number-input-wrapper' style={{paddingRight: '0px'}}>
                <AuInputLabel text='Year' htmlFor='Year' />
                <div>
                  <input
                    type='text'
                    placeholder='YYYY'
                    onChange={handleOnChangeYear}
                    value={year}
                    disabled={disabled}
                    className={
                      `au-text-input ${className} au-text-input--number au-text-input--width-lg` +
                      `${dark ? ' au-text-input--dark' : ''}` +
                      `${getFieldMeta(name).error && !disabled ? ' au-text-input--invalid' : ''}`
                    } />
                </div>
              </div>
            </div>
            {
              fieldError && !disabled &&
              <AuErrorText text={get(errors, name)} />
            }
          </>
        )}
      </Field>
    </AuFormItemWrapper>
  );
};

export default AuDateInput;